import { Badge, Card, MicroLabel } from '@codezo/quote-detail';
import { Fragment, type ReactNode } from 'react';
import type { Tone } from './types';

export interface OrderDeliveryRow {
	key: string;
	/**
	 * Which block this row belongs under — "Samples", "Shipments".
	 *
	 * A heading is drawn whenever the value CHANGES down the list, so the caller
	 * groups by ordering rather than by handing over nested arrays. Rows are still
	 * rendered exactly in the order given; this only labels where one run ends and
	 * the next begins. Omit it on every row and the list is flat, as before.
	 */
	group?: string;
	/** Server-worded — "Canvas Tote sample, round 2", "Shipment 2". */
	reference: string;
	state: { label: string; tone: Tone };
	/**
	 * What is travelling — "100 units", or "Canvas Tote × 250 · Enamel Mug × 100"
	 * on a box carrying more than one product.
	 *
	 * Single-product boxes do NOT repeat the product name: the row's title has
	 * already said it, and "Keychain × 100" under a heading reading "Keychain" is
	 * the line that made this block look like noise.
	 */
	contents?: ReactNode;
	/**
	 * When, as a LABEL and a value — `{ label: 'Arrived', value: 'Sep 17, 2026' }`.
	 *
	 * Split rather than pre-worded into one string. A bare date under a title is
	 * the thing nobody could read: "Sep 17, 2026" says nothing about whether it
	 * happened or is hoped for, and the pill that answers it sits at the far end of
	 * the row above. The verb is the key, so the row can set it in the label column
	 * with every other key and the date stops being an orphan.
	 *
	 * Still the caller's words: only it knows whether it has a fact or a forecast,
	 * and a virtual sample is SUBMITTED where a box ARRIVES.
	 */
	when?: { label: ReactNode; value: ReactNode };
	/** True while `when` is a forecast — draws it as one. */
	projected?: boolean;
	/**
	 * How it travels — "Express", "Standard", "Sea freight".
	 *
	 * Sits WITH the timing rather than with the contents, because it is the
	 * reason for the timing: a window that looks slow is usually answered by
	 * this word, and the two have to be read together for that to land.
	 */
	method?: ReactNode;
	/**
	 * Carrier and number, and the way to follow it.
	 *
	 * Same shape a sample round's tracking takes, for the same reason: the
	 * control goes UNDER the number rather than after it. A tracking number runs
	 * long, and a button crammed onto the end of it squeezes both and reads as an
	 * afterthought. Stacked, it aligns to the value column and belongs to the row
	 * it sits in.
	 */
	tracking?: { label: ReactNode; action?: ReactNode };
	/** Greyed and struck through rather than hidden. */
	cancelled?: boolean;
	/**
	 * What can be done to the thing this row is about.
	 *
	 * The list itself is a display projection — shipments, physical samples and
	 * virtual samples flattened into one timeline so arrivals read in sequence —
	 * and three kinds of record cannot share one action set. So this component
	 * neither derives nor ranks anything: the caller joins the row back to the
	 * record it came from, reads that record's own action list, and hands over
	 * the controls already ordered.
	 *
	 * Absent on a row whose record offers nothing, which is most of them.
	 */
	actions?: ReactNode;
}

/**
 * One key and its value, on a fixed label column.
 *
 * The column is what makes the card readable: four rows of values starting at
 * the same x can be scanned down, where four sentences of different lengths
 * cannot. 64px fits "Contents" and "Submitted" at this size without wrapping,
 * which are the longest keys in use.
 */
function Detail({ label, children }: { label: ReactNode; children: ReactNode }) {
	return (
		<div className="mt-1 flex items-baseline gap-2 text-2xs">
			<span className="w-16 shrink-0 text-text-4">{label}</span>
			<span className="min-w-0 flex-1 text-text-2">{children}</span>
		</div>
	);
}

/**
 * Everything an order is waiting to receive.
 *
 * ── One row per MOVEMENT, not per product ──
 *
 * A box carrying four products is one row that lists all four; a rejected sample
 * round and its replacement are two rows, because two things were made and two
 * were sent. That is the server's own shape and it is the right one — a reader
 * asks "what is coming and when", and the answer is a list of arrivals, not a
 * list of products each repeating the box it happens to be in.
 *
 * ── What a row leads with ──
 *
 * The name, then one sentence: where it has got to, and when. Everything else is
 * detail set beneath it.
 *
 * ── Three sizes, and only three ──
 *
 * Name at `sm`, timing at `xs`, everything else at `2xs`. The version before
 * this had the name at `sm` and every other line at `2xs`, which is not a
 * hierarchy but a cliff: four rows of identical grey with nothing to land on.
 *
 * The state pill sits with the NAME, which is where a reader looks for it: what
 * it is and where it has got to, read together. It was briefly demoted to a dot,
 * on the theory that four pills were wallpaper — but a 6px dot is not scannable,
 * and scanning state is the first thing anyone does here. It then spent a
 * version on the timing line, which only moved the imbalance.
 *
 * What was actually wrong was the LAYOUT: both sat in one `flex-wrap` row, so a
 * title long enough to wrap took the pill with it. Two columns fix it — the
 * title wraps inside its own, the pill is `shrink-0` against the edge.
 *
 * ── A projection is not a promise ──
 *
 * While `projected`, the window is production plus transit anchored on TODAY, so
 * it slides out each day nothing happens. That is the honest statement, but only
 * if it has never been shown as a date. So a projected row says "expected" and
 * is set quieter than a fact; only an arrival is stated plainly.
 *
 * ── Why it is grouped ──
 *
 * Flat, the list alternates "Keychain sample", "Keychain", "test sample",
 * "test": four rows where every other one is a different KIND of thing, told
 * apart by one word inside a title. A sample is one unit coming for approval and
 * a shipment is the goods — different sizes, different consequences, different
 * questions — and the page already splits them everywhere else it counts money,
 * in the summary lines, the payment buckets and the payment routes. A reader who
 * has learned that split downstairs should not meet it undone here.
 *
 * Rows are rendered in the order given and a heading appears where `group`
 * changes, so the ordering is the caller's. Cancelled rows come through too; the
 * caller decides whether to show them, and this greys rather than hides the ones
 * it is handed.
 */
export function OrderDeliveries({
	deliveries,
	label = 'Deliveries',
	emptyState,
	note,
}: {
	deliveries: OrderDeliveryRow[];
	label?: string;
	emptyState?: ReactNode;
	/** The address, or anything else the caller wants under the list. */
	note?: ReactNode;
}) {
	return (
		<Card>
			<div className="border-b border-line-1 px-4 py-3">
				<MicroLabel>{label}</MicroLabel>
			</div>

			{deliveries.length === 0 ? (
				<div className="px-4 py-4 text-sm text-text-4">
					{emptyState ?? 'Nothing is on its way yet.'}
				</div>
			) : (
				deliveries.map((row, i) => (
					<Fragment key={row.key}>
						{row.group && row.group !== deliveries[i - 1]?.group && (
							<div className="border-b border-line-1 bg-surface-sunken px-4 py-1.5">
								<MicroLabel className="text-text-3">{row.group}</MicroLabel>
							</div>
						)}
						<div
							className={
								'border-b border-line-1 px-4 py-3 last:border-b-0' +
								(row.cancelled ? ' opacity-55' : '')
							}
						>
							{/* Name and state on one row — what it is and where it has got
						    to, read together.
						    `items-center`, and no `flex-wrap`. The original had both:
						    the wrap is what broke it, because a title long enough to
						    push the pill sent it to a line of its own, floating
						    mid-name. Without the wrap the title is `min-w-0 flex-1` and
						    breaks inside its own column while the pill holds the edge,
						    so centring is just centring — no nudge, no line-box
						    arithmetic. A two-line title centres the pill across both,
						    which is the right answer anyway: it belongs to the name, not
						    to the name's first line. */}
							<div className="flex items-center gap-2">
								<span
									className={
										'min-w-0 flex-1 text-sm leading-snug text-text-1' +
										(row.cancelled ? ' line-through' : '')
									}
								>
									{row.reference}
								</span>
								<Badge tone={row.state.tone} dot>
									{row.state.label}
								</Badge>
							</div>

							{/* Everything under the title is a KEY and a VALUE, in one
						    label column.

						    It used to be three bare strings stacked — a date, a
						    "Keychain × 100 · Standard", a carrier and a number — none of
						    which said what it was. A reader had to infer that the date
						    meant arrival from a pill at the other end of the row above,
						    and that the last line was a tracking number from the shape of
						    it. Naming each one costs a narrow column and answers the
						    question the block exists to answer.

						    The label column is fixed, so the values line up down the card
						    and the rows can be read as a table rather than four sentences.
						    A projection is set quieter than a fact. */}
							{row.when && (
								<Detail label={row.when.label}>
									<span
										className={
											'font-mono tabular-nums ' +
											(row.projected ? 'text-text-3' : 'text-text-1')
										}
									>
										{row.when.value}
									</span>
								</Detail>
							)}

							{(row.contents || row.method) && (
								<Detail label="Contents">
									{row.contents}
									{row.contents && row.method ? ' · ' : ''}
									{row.method}
								</Detail>
							)}

							{row.tracking && <Detail label="Tracking">{row.tracking.label}</Detail>}

							{/* ONE control line, holding everything this row can do —
							    following the tracking number, and the way to correct
							    it, side by side.

							    They were two rows for a while, because they arrived
							    separately: tracking brought its own "Track", and the
							    actions came later and took a row of their own. Each
							    was reasonable alone and together they read as a
							    stack of unrelated controls under a single number,
							    with the second one floating free of anything naming
							    it. They are the same kind of thing — what to do
							    about this delivery — so they sit on the same line.

							    Controls come LAST: a reader scans state, timing and
							    tracking to answer "where is it", and only then acts.
							    An empty label holds the value column without a
							    hand-measured indent, so the line cannot drift from
							    the rows above it. */}
							{(row.tracking?.action || row.actions) && (
								<Detail label="">
									<span className="flex flex-wrap items-center gap-x-2 gap-y-1">
										{row.tracking?.action}
										{row.actions}
									</span>
								</Detail>
							)}
						</div>
					</Fragment>
				))
			)}

			{note && <div className="border-t border-line-1 px-4 py-3">{note}</div>}
		</Card>
	);
}
